import { makeAutoObservable, runInAction } from "mobx";
import { getHistoryAnalysis, getHistoryRequest, getHistoryText } from "requests";
import type { RootStore } from "stores/rootStore";
import { copyClipboard } from "utils";

/** A row of the list: what it shows of a scan — the scan itself is fetched on Restore. */
export interface HistoryItem {
  id: number | string;
  /** The text's beginning; `text_truncated` says whether it goes on. */
  text: string;
  text_truncated: boolean;
  date_created: string;
  ai_check?: boolean;
  plagiarism_check?: boolean;
  ai_score?: number;
  plagiarism_score?: number;
  grammar_check?: boolean;
  /** The issues the Grammar panel shows; null while the check runs or when it failed. */
  grammar_issues?: number;
  analysis_uuid: string;
  file_name?: string;
  words_count?: number;
}

interface HistoryApiResponse {
  results: HistoryItem[];
  count: number;
}

export class HistoryStore {
  root: RootStore;
  history: HistoryItem[] = [];
  loading: boolean = false;
  page: number = 1;
  totalPages: number = 0;
  pageSize: number;
  /** The scan Restore is fetching, or asking to replace the page with — its row's spinner. */
  restoring: string | null = null;
  /** Whole texts of long rows, by scan, fetched when one is opened or copied. */
  fullTexts = new Map<string, string>();
  /** Those still on their way: opening a row and copying it share one request. */
  private fetchingTexts = new Map<string, Promise<string>>();

  constructor(root: RootStore) {
    this.root = root;
    this.pageSize = 5;
    makeAutoObservable<HistoryStore, 'fetchingTexts'>(this, {fetchingTexts: false});
  }

  /** Put a past scan back on the scan page the way it finished: its whole text and every
   *  result it has, fetched now — the list holds a preview only — once the page may be
   *  replaced (`Replace current text?`). Resolves whether it is on the page; a failed fetch is
   *  told in a toast. A later Restore, or `cancelRestore`, takes over from one still loading,
   *  which then ends unseen. */
  async restore(item: HistoryItem): Promise<boolean> {
    const uuid = item.analysis_uuid;
    this.restoring = uuid;
    const scan = await getHistoryAnalysis(uuid).catch(e => {
      if (this.restoring === uuid) this.root.toast.error('history-restore-error', e);
      return null;
    });
    const replace = !!scan && this.restoring === uuid
      && await this.root.input.confirmReplace(
        scan.text, 'history-replace-body', this.root.input.analysisSections(scan.items));
    // Given up on while it loaded or while the confirm was open: the page is someone else's now.
    if (this.restoring !== uuid) return false;
    runInAction(() => {
      this.restoring = null;
      if (!replace) return;
      this.root.input.openAnalysis({uuid, date: scan.date, text: scan.text, items: scan.items});
      this.root.layout.setWorkspace('scan');
    });
    return replace;
  }

  /** The Restore still loading is no longer wanted: it lands nowhere. */
  cancelRestore() {
    this.restoring = null;
  }

  /** The row's whole text if it is at hand: the row's own, or a long one fetched before. */
  knownText(item: HistoryItem): string | undefined {
    return item.text_truncated ? this.fullTexts.get(item.analysis_uuid) : item.text;
  }

  /** The row's whole text — a long one is fetched the first time it is wanted. A failed
   *  fetch is told in a toast and keeps nothing, so the next try fetches again. */
  fullText(item: HistoryItem): Promise<string> {
    const known = this.knownText(item);
    if (known !== undefined) return Promise.resolve(known);
    const uuid = item.analysis_uuid;
    let fetching = this.fetchingTexts.get(uuid);
    if (!fetching) {
      fetching = getHistoryText(uuid).then(({text}) => {
        runInAction(() => this.fullTexts.set(uuid, text));
        return text;
      }, e => {
        this.root.toast.error('history-text-error', e);
        throw e;
      }).finally(() => this.fetchingTexts.delete(uuid));
      this.fetchingTexts.set(uuid, fetching);
    }
    return fetching;
  }

  /** The row's whole text to the clipboard. */
  copy(item: HistoryItem): Promise<boolean> {
    return copyClipboard(this.knownText(item) ?? this.fullText(item));
  }

  setPage(page: number) {
    this.page = page;
    this.loadHistory();
  }

  setPageSize(pageSize: number) {
    this.pageSize = pageSize;
    this.loadHistory();
  }

  /** A `quiet` load — the refresh after a scan, with no one asking for the list — fails
   *  unseen; any other is told in a toast. The list keeps its rows either way. */
  async loadHistory({quiet = false}: {quiet?: boolean} = {}) {
    // An anon's history page is a sign-in prompt, not the list.
    if (!this.root.user.user?.history_retention || this.root.user.isAnonymous) {
      return
    }
    this.loading = true;
    try {
      const data = (await getHistoryRequest(this.page, this.pageSize)) as Partial<HistoryApiResponse>;
      this.history = (data?.results as HistoryItem[]) || [];
      this.totalPages = (data?.count as number) || 0;
    } catch (e) {
      if (!quiet) this.root.toast.error('history-load-error', e);
    } finally {
      this.loading = false;
    }
  }
}
